import React, {
  FC,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
  SetStateAction,
} from "react";
import { tap, debounceTime, distinctUntilChanged } from "rxjs/operators";
import { makeStyles, createStyles } from "@material-ui/styles";
import {
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField as MuiTextField,
  Theme,
} from "@material-ui/core";
import NumberFormat from "react-number-format";
import { Subject } from "rxjs";
import ClearIcon from "@material-ui/icons/Cancel";
import { TextFieldControlDefinition } from "../services/layout";
import { DataNode } from "../services/form";

import { useDataValue, useEditability, useDataFieldValidity } from "../utils";
import { FormContext } from "../FormContext";
import { FormControlProps } from "../utils";
import { FormControl } from "./FormControl";
import { registry } from "../registry";
import { DFSExpression, parseFieldIdentifier, walkData } from "../services";

// hack, todo bring in defs from core, use a daft expression for this purpose
export interface NITextFieldControlDefinition
  extends TextFieldControlDefinition {
  requiredPlaceholder?: string;
  expression?: DFSExpression;
  showClear?: boolean;
  clearValues?: string[];
  numRows?: number;
  autoComplete?: string;
  tableCell?: boolean;
}
// todo bring field typings in from core
export interface TextFieldProps
  extends FormControlProps<
    NITextFieldControlDefinition & { debounceTime?: number }
  > {}

export function useTraceUpdate(props: any) {
  const prev = useRef(props);
  useEffect(() => {
    const changedProps = Object.entries(props).reduce(
      (ps: any, [k, v]: any) => {
        if (prev.current[k] !== v) {
          ps[k] = [prev.current[k], v];
        }
        return ps;
      },
      {}
    );
    if (Object.keys(changedProps).length > 0) {
      console.log("Changed props:", changedProps);
    }
    prev.current = props;
  });
}

const useStyles = makeStyles(function (theme: Theme) {
  return createStyles({
    text: function (props: { editable: boolean; dirty: boolean }) {
      return {
        margin: 0,
        backgroundColor: props.editable ? theme.palette.common.white : theme.palette.grey[100],
        color: props.dirty
          ? theme.palette.text.disabled
          : theme.palette.text.primary,
      };
    },
    isDefault: {
      "font-style": "italic",
    },
  });
});

const NumericInput = (props: any) => {
  return (
    <OutlinedInput
      inputProps={{ inputMode: "decimal" }}
      className={props.className}
      labelWidth={0}
      placeholder={props.placeholder}
      multiline={props.multiline}
      onChange={props.onChange}
      value={props.value}
      margin={"dense"}
      disabled={props.disabled}
    />
  );
};

export const TextField: FC<TextFieldProps> = function (props) {
  const definition = props.definition;
  const { expMgr, onSetData, scope, form } = useContext(FormContext);
  const officialValue = useDataValue(definition.dataPath);
  const [text, setText] = useState("");
  const [defaultValue, setDefaultValue] = useState("");
  const [valueEqualsDefault, setValueEqualsDefault] = useState(false);
  const expression = definition.expression;
  const subject = useMemo(
    () => new Subject<SetStateAction<string>>(),
    [expMgr]
  );
  const numRows =
    definition.multiline && definition.numRows ? definition.numRows : 1;

  /**
   * Traverses the node definition to extract the default value for the datapath
   */
  useEffect(() => {
    const i = /\/properties\//g;
    walkData(
      form.definition.data,
      function visitor(node: DataNode, nodeName: string) {
        if (nodeName === "") {
          return;
        }
        const s = nodeName.replace(i, "/");
        if (
          s === parseFieldIdentifier(definition.dataPath) &&
          typeof node.default !== "undefined"
        ) {
          setDefaultValue(node.default);
        }
      }
    );
  }, [form.definition.data, definition.dataPath]);

  /**
   * Determines if the current value equals the default value from the definition
   */
  useEffect(() => {
    setValueEqualsDefault(officialValue == defaultValue);
  }, [officialValue, defaultValue]);

  useEffect(() => {
    if (!expression) {
      return;
    }
    const sub = expMgr!
      .watch(expression, scope)
      .pipe(
        debounceTime(200),
        tap((computed) => {
          onSetData(definition.dataPath, scope, computed);
        })
      )
      .subscribe();

    return () => {
      sub.unsubscribe();
    };
  }, [expMgr, expression, scope, subject]);

  useEffect(
    function updateFromSource() {
      if (keyboard === "number") {
        const asLocaleString =
          officialValue !== undefined
            ? Number(officialValue)?.toLocaleString("en-US", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 10,
              })
            : "";

        setText("" + asLocaleString);
      } else {
        setText(
          "" +
            (officialValue !== undefined && officialValue !== null
              ? officialValue
              : "")
        );
      }
    },
    [officialValue]
  );

  const editable = useEditability(definition.editable);
  const keyboard = transcribeKeyword(definition.keyboard as "keyboard");
  const interpreter = useMemo(
    function () {
      if (keyboard === "number") {
        return function (val: any) {
          if (val === null || val === undefined || val === "") {
            return undefined;
          }
          let parsed;
          if (val.indexOf(",") > 0) {
            parsed = val.replace(/,/g, "");
          } else {
            parsed = val;
          }
          return parsed.indexOf(".") < 0
            ? Number.parseInt(parsed)
            : Number.parseFloat(parsed);
        };
      }
      return function (e: any) {
        return e === "" ? undefined : e;
      };
    },
    [keyboard]
  );

  const [dirty, setDirty] = useState(false);

  useEffect(
    function trackDirtyState() {
      setDirty(interpreter(text) !== officialValue);
    },
    [text, officialValue]
  );

  useEffect(
    function handleTextChange() {
      const t = subject
        .pipe(
          tap((e) => {
            setText(e);
          }),
          debounceTime(definition.debounceTime ?? 1200),
          distinctUntilChanged()
        )
        .subscribe((newText) => {
          const interpreted = interpreter(newText);
          onSetData(
            definition.dataPath,
            scope,
            interpreted === undefined ? null : interpreted
          );
        });
      return function () {
        t.unsubscribe();
      };
    },
    [subject, definition.dataPath, definition.debounceTime]
  );

  const inputRef = useRef(null);
  const error = useDataFieldValidity(definition.dataPath);
  const classes = useStyles({
    editable: editable,
    dirty: dirty,
  });

  // numbers are allowed to be formatted as strings (i.e. 1,000,000.00)
  if (keyboard === "number") {
    return (
      <FormControl
        label={definition.label}
        helpText={definition.helpText}
        footnote={definition.footnote}
        style={definition.style}
        error={error}
      >
        <NumberFormat
          thousandSeparator={true}
          decimalScale={10}
          customInput={NumericInput}
          // inputs below are all for the component (OutlinedInput) that is passed to NumebrFormat
          className={[
            classes.text,
            valueEqualsDefault ? classes.isDefault : "",
          ].join(" ")}
          placeholder={
            error === "Required" && definition.requiredPlaceholder
              ? definition.requiredPlaceholder
              : definition.placeholder
          }
          multiline={definition.multiline}
          onChange={(e) => {
            subject.next(e.target.value);
          }}
          value={text}
          disabled={!editable}
        />
      </FormControl>
    );
  }

  return (
    <FormControl
      label={definition.label}
      helpText={definition.helpText}
      footnote={definition.footnote}
      style={definition.style}
      error={error}
    >
      <OutlinedInput
        className={[
          classes.text,
          valueEqualsDefault ? classes.isDefault : "",
        ].join(" ")}
        labelWidth={0}
        inputRef={inputRef}
        placeholder={
          error === "Required" && definition.requiredPlaceholder
            ? definition.requiredPlaceholder
            : definition.placeholder
        }
        multiline={definition.multiline}
        onChange={(e) => subject.next(e.target.value)}
        value={text}
        autoComplete={
          definition.autoComplete ? definition.autoComplete : undefined
        }
        margin={"dense"}
        type={keyboard}
        rows={numRows}
        rowsMax={10}
        disabled={!editable}
        endAdornment={
          definition.showClear ? (
            <InputAdornment position="end">
              <IconButton
                color="inherit"
                onClick={() => {
                  if (definition.clearValues) {
                    definition.clearValues?.forEach(function (value: string) {
                      onSetData(value, scope, null);
                    });
                  } else {
                    onSetData(definition.dataPath, scope, null);
                  }
                }}
                disabled={!text}
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ) : null
        }
      />
    </FormControl>
  );
};

function transcribeKeyword(e: string) {
  switch (e) {
    case "numeric":
    case "number":
      return "number";
    case "phone":
    case "tel":
    case "telephone":
      return "tel";
    case "email":
      return "email";
    default:
      return "text";
  }
}

registry.registerComponent("TextField", TextField);
